/**
 * Actuals → re-projection: the discipline loop.
 *
 * Turns recorded actual payments into a forward answer: where is the loan
 * REALLY (balance as of today), when does it really pay off, and what does
 * the deviation cost or save in interest versus the committed plan.
 *
 * Amortization semantics deliberately mirror the planning engine
 * (`calculationEngine.buildStandardLoanAmortization`): periodic rate =
 * annual / paymentsPerYear, interest accrues per period on the running
 * balance, `balance = balance·(1+r) − payment`, floored at zero. When every
 * due payment matches the plan, these numbers agree with the planned
 * schedule to the cent.
 *
 * Policy (surfaced via `unrecordedDueCount`): a due payment with no recorded
 * actual is assumed paid as planned — families record exceptions, not every
 * payment. "Skipped" pays 0; anything else pays `actual_amount` when
 * recorded, else the planned amount.
 */
import {
  getNextPaymentDate,
  getPaymentsPerYear,
  type PaymentFrequency,
} from "@/lib/calculationEngine";

export interface PlannedPaymentRow {
  paymentNumber: number;
  date: string; // ISO YYYY-MM-DD
  amount: number;
}

export interface ActualPaymentRecord {
  payment_number: number;
  status: "paid_as_planned" | "paid_different" | "skipped" | "paid_late" | "partial";
  actual_amount: number | null;
  actual_date?: string | null;
  /** Late charge assessed on this payment. */
  late_fee?: number | null;
  /** Returned / NSF cheque charge assessed on this payment. */
  nsf_fee?: number | null;
  /** "paid_with_payment" (settled separately) or "capitalized" (added to balance). */
  fee_treatment?: "paid_with_payment" | "capitalized" | null;
  payment_method?: string | null;
  payment_reference?: string | null;
  note?: string | null;
}

/** Fees that the Lender elected to add to the outstanding balance. */
export function capitalizedFees(actual: ActualPaymentRecord | undefined): number {
  if (!actual || actual.fee_treatment !== "capitalized") return 0;
  const late = Number(actual.late_fee) || 0;
  const nsf = Number(actual.nsf_fee) || 0;
  return Math.max(0, late) + Math.max(0, nsf);
}

/** All fees assessed on a payment, however they were settled. */
export function totalFees(actual: ActualPaymentRecord | undefined): number {
  if (!actual) return 0;
  return Math.max(0, Number(actual.late_fee) || 0) + Math.max(0, Number(actual.nsf_fee) || 0);
}

export interface ReprojectionInput {
  borrowedAmount: number;
  annualRate: number; // decimal, e.g. 0.12
  paymentFrequency: PaymentFrequency;
  planned: PlannedPaymentRow[];
  actuals: ActualPaymentRecord[];
  asOf: string; // ISO YYYY-MM-DD
}

export type Pace = "on_track" | "ahead" | "behind";

export interface Reprojection {
  /** Planned payments due on or before `asOf`. */
  dueCount: number;
  /** Due payments with no recorded actual — assumed paid as planned. */
  unrecordedDueCount: number;
  /** Balance today if every due payment had been made exactly as planned. */
  plannedBalance: number;
  /** Balance today given the recorded actuals. */
  actualBalance: number;
  /** actualBalance − plannedBalance; positive means behind plan. */
  balanceVariance: number;
  pace: Pace;
  /** Last planned payment date (null for an empty plan). */
  plannedPayoffDate: string | null;
  /** Projected real payoff continuing at the planned payment; null when the
   *  payment no longer covers interest (see `coversInterest`). */
  projectedPayoffDate: string | null;
  /** Projected payments still needed minus payments the plan still expects.
   *  Negative = paying off early. */
  projectedExtraPayments: number;
  /** Projected future interest minus the plan's future interest.
   *  Negative = interest saved. */
  projectedExtraInterest: number;
  /** False when the planned payment cannot outrun period interest. */
  coversInterest: boolean;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Balances below half a cent are float residue, not debt. */
const clampPaid = (bal: number) => (bal < 0.005 ? 0 : bal);

/** Hard stop for payoff simulation — beyond this the payment plainly
 *  cannot retire the balance in any human timeframe. */
const MAX_PROJECTED_PERIODS = 12_00; // 100 years of monthly payments

function effectivePaid(row: PlannedPaymentRow, actual: ActualPaymentRecord | undefined): number {
  if (!actual) return row.amount; // unrecorded → assume as planned
  if (actual.status === "skipped") return 0;
  const amount = actual.actual_amount;
  if (amount == null || !Number.isFinite(amount) || amount < 0) return row.amount;
  return amount;
}

/** Walk future interest cost of finishing the planned schedule from `balance`. */
function plannedFutureInterest(
  balance: number,
  remaining: PlannedPaymentRow[],
  periodicRate: number,
): number {
  let bal = balance;
  let interest = 0;
  for (const row of remaining) {
    if (bal <= 0) break;
    const i = bal * periodicRate;
    interest += i;
    bal = clampPaid(Math.max(0, bal + i - row.amount));
  }
  return interest;
}

export function reprojectFromActuals(input: ReprojectionInput): Reprojection {
  const { planned, asOf } = input;
  const periodicRate = input.annualRate / getPaymentsPerYear(input.paymentFrequency);
  const actualByNumber = new Map(input.actuals.map((a) => [a.payment_number, a]));

  const due = planned.filter((p) => p.date <= asOf);
  const remaining = planned.filter((p) => p.date > asOf);
  const plannedPayoffDate = planned.length > 0 ? planned[planned.length - 1].date : null;

  if (planned.length === 0) {
    return {
      dueCount: 0,
      unrecordedDueCount: 0,
      plannedBalance: 0,
      actualBalance: 0,
      balanceVariance: 0,
      pace: "on_track",
      plannedPayoffDate: null,
      projectedPayoffDate: null,
      projectedExtraPayments: 0,
      projectedExtraInterest: 0,
      coversInterest: true,
    };
  }

  // ── walk the due portion on both paths
  let plannedBal = input.borrowedAmount;
  let actualBal = input.borrowedAmount;
  let unrecorded = 0;
  let paidOffOn: string | null = null;

  for (const row of due) {
    const actual = actualByNumber.get(row.paymentNumber);
    if (!actual) unrecorded += 1;

    plannedBal = clampPaid(Math.max(0, plannedBal * (1 + periodicRate) - row.amount));

    if (actualBal > 0) {
      const grown = actualBal * (1 + periodicRate) - effectivePaid(row, actual) + capitalizedFees(actual);
      actualBal = clampPaid(Math.max(0, grown));
      if (actualBal === 0 && paidOffOn === null) paidOffOn = row.date;
    }
  }

  const balanceVariance = round2(actualBal - plannedBal);
  const pace: Pace = balanceVariance > 1 ? "behind" : balanceVariance < -1 ? "ahead" : "on_track";

  // ── project forward from the actual balance at the planned payment cadence
  const plannedRemainingCount = remaining.length;
  const planFutureInterest = plannedFutureInterest(plannedBal, remaining, periodicRate);

  let projectedPayoffDate: string | null;
  let projectedPeriods = 0;
  let projectedInterest = 0;
  let coversInterest = true;

  if (actualBal <= 0) {
    projectedPayoffDate = paidOffOn ?? (due.length > 0 ? due[due.length - 1].date : asOf);
  } else {
    // Payment cadence continues the planned schedule; if actuals outlast it,
    // keep the last planned amount and extend dates at the same frequency.
    const lastAmount = planned[planned.length - 1].amount;
    const standardAmount = remaining[0]?.amount ?? lastAmount;
    let bal = actualBal;
    let date = due.length > 0 ? due[due.length - 1].date : planned[0].date;
    let idx = 0;
    projectedPayoffDate = null;

    while (bal > 0 && projectedPeriods < MAX_PROJECTED_PERIODS) {
      const row = remaining[idx];
      const amount = row?.amount ?? standardAmount;
      date = row?.date ?? getNextPaymentDate(date, input.paymentFrequency);
      const i = bal * periodicRate;
      if (amount <= i && bal >= actualBal) {
        // Not even holding the line — balance can only grow.
        coversInterest = false;
        break;
      }
      projectedInterest += i;
      bal = clampPaid(Math.max(0, bal + i - amount));
      projectedPeriods += 1;
      idx += 1;
      if (bal === 0) projectedPayoffDate = date;
    }
    if (projectedPeriods >= MAX_PROJECTED_PERIODS) {
      coversInterest = false;
      projectedPayoffDate = null;
    }
    if (!coversInterest) {
      projectedPayoffDate = null;
      projectedInterest = 0;
    }
  }

  return {
    dueCount: due.length,
    unrecordedDueCount: unrecorded,
    plannedBalance: round2(plannedBal),
    actualBalance: round2(actualBal),
    balanceVariance,
    pace,
    plannedPayoffDate,
    projectedPayoffDate,
    projectedExtraPayments: coversInterest ? projectedPeriods - plannedRemainingCount : 0,
    projectedExtraInterest: coversInterest ? round2(projectedInterest - planFutureInterest) : 0,
    coversInterest,
  };
}

/* ------------------------------------------------------------------ */
/*  As-tracked amortization: history + re-amortized forward schedule    */
/* ------------------------------------------------------------------ */

export interface AsTrackedRow {
  paymentNumber: number;
  /** "history" = on/before asOf (reflects what was actually recorded);
   *  "projected" = forward re-amortization from today's real balance. */
  section: "history" | "projected";
  date: string;
  plannedAmount: number | null;
  paymentAmount: number;
  interest: number;
  principal: number;
  /** Fees assessed on this payment (late + NSF). */
  fees: number;
  /** Portion of `fees` that was added to the balance. */
  feesCapitalized: number;
  balance: number;
  status: ActualPaymentRecord["status"] | null;
  /** True when no actual was recorded and the plan amount was assumed. */
  assumed: boolean;
  method: string | null;
  reference: string | null;
  note: string | null;
}

export interface AsTrackedSchedule {
  rows: AsTrackedRow[];
  /** Interest actually accrued through `asOf`. */
  interestToDate: number;
  /** Interest still to come on the re-amortized path. */
  projectedFutureInterest: number;
  totalFeesAssessed: number;
  totalFeesCapitalized: number;
  payoffDate: string | null;
  /** True when the forward payment no longer covers period interest. */
  stalled: boolean;
}

/**
 * Builds a single continuous amortization that tells the truth:
 * recorded reality up to `asOf`, then a re-amortization of the REAL
 * remaining balance at the planned cadence. Extra payments therefore
 * visibly shorten the tail; missed payments visibly lengthen it.
 */
export function buildAsTrackedSchedule(input: ReprojectionInput): AsTrackedSchedule {
  const { planned, asOf } = input;
  const periodicRate = input.annualRate / getPaymentsPerYear(input.paymentFrequency);
  const actualByNumber = new Map(input.actuals.map((a) => [a.payment_number, a]));
  const rows: AsTrackedRow[] = [];

  if (planned.length === 0) {
    return {
      rows, interestToDate: 0, projectedFutureInterest: 0,
      totalFeesAssessed: 0, totalFeesCapitalized: 0, payoffDate: null, stalled: false,
    };
  }

  const due = planned.filter((p) => p.date <= asOf);
  const remaining = planned.filter((p) => p.date > asOf);

  let bal = input.borrowedAmount;
  let interestToDate = 0;
  let feesAssessed = 0;
  let feesCapitalized = 0;
  let payoffDate: string | null = null;

  for (const row of due) {
    const actual = actualByNumber.get(row.paymentNumber);
    const fees = totalFees(actual);
    const capFees = capitalizedFees(actual);
    feesAssessed += fees;
    feesCapitalized += capFees;

    if (bal <= 0) {
      rows.push({
        paymentNumber: row.paymentNumber, section: "history", date: actual?.actual_date || row.date,
        plannedAmount: row.amount, paymentAmount: 0, interest: 0, principal: 0,
        fees, feesCapitalized: capFees, balance: 0,
        status: actual?.status ?? null, assumed: !actual,
        method: actual?.payment_method ?? null, reference: actual?.payment_reference ?? null,
        note: actual?.note ?? null,
      });
      continue;
    }

    const interest = bal * periodicRate;
    const paid = effectivePaid(row, actual);
    interestToDate += interest;
    const opening = bal;
    bal = clampPaid(Math.max(0, bal + interest - paid + capFees));
    if (bal === 0 && payoffDate === null) payoffDate = actual?.actual_date || row.date;

    rows.push({
      paymentNumber: row.paymentNumber,
      section: "history",
      date: actual?.actual_date || row.date,
      plannedAmount: row.amount,
      paymentAmount: round2(paid),
      interest: round2(interest),
      principal: round2(Math.max(0, opening + capFees - bal)),
      fees,
      feesCapitalized: capFees,
      balance: round2(bal),
      status: actual?.status ?? null,
      assumed: !actual,
      method: actual?.payment_method ?? null,
      reference: actual?.payment_reference ?? null,
      note: actual?.note ?? null,
    });
  }

  // ── forward re-amortization from the REAL balance
  let projectedInterest = 0;
  let stalled = false;
  if (bal > 0) {
    const lastAmount = planned[planned.length - 1].amount;
    const standardAmount = remaining[0]?.amount ?? lastAmount;
    let date = due.length > 0 ? due[due.length - 1].date : planned[0].date;
    let idx = 0;
    let n = due.length > 0 ? due[due.length - 1].paymentNumber : 0;
    const startBal = bal;

    while (bal > 0 && rows.length < 2000) {
      const row = remaining[idx];
      const amount = row?.amount ?? standardAmount;
      date = row?.date ?? getNextPaymentDate(date, input.paymentFrequency);
      const interest = bal * periodicRate;
      if (amount <= interest && bal >= startBal) {
        stalled = true;
        break;
      }
      const opening = bal;
      const applied = Math.min(amount, opening + interest);
      bal = clampPaid(Math.max(0, opening + interest - applied));
      projectedInterest += interest;
      n += 1;
      idx += 1;
      rows.push({
        paymentNumber: n,
        section: "projected",
        date,
        plannedAmount: row?.amount ?? null,
        paymentAmount: round2(applied),
        interest: round2(interest),
        principal: round2(Math.max(0, opening - bal)),
        fees: 0,
        feesCapitalized: 0,
        balance: round2(bal),
        status: null,
        assumed: false,
        method: null,
        reference: null,
        note: null,
      });
      if (bal === 0) payoffDate = date;
    }
  }

  return {
    rows,
    interestToDate: round2(interestToDate),
    projectedFutureInterest: stalled ? 0 : round2(projectedInterest),
    totalFeesAssessed: round2(feesAssessed),
    totalFeesCapitalized: round2(feesCapitalized),
    payoffDate: stalled ? null : payoffDate,
    stalled,
  };
}
